import { memo, MutableRefObject, useEffect, useState } from 'react'
import { TLastValuesDataRef } from '@/services/data/data.types.ts'
import { ITextWidget } from '../../dashboards.types'
import css from './textChart.module.css'
import useSkipFirstRenderEffect from '../../../../shared/hooks/useSkipFirstRenderEffect'
import { createEditor, Descendant } from 'slate'
import { useRecoilValue } from 'recoil'
import parametersSelectors from '@/pages/Parameters/parameters.selectors'
import { ITextWidgetParameter } from './textChart.types'
import { Editable, Slate, withReact } from 'slate-react'
import RenderElement from './RichTextEditor/components/RenderElement'
import RenderLeaf from './RichTextEditor/components/RenderLeaf'
import parametersUtils from '@/pages/Parameters/parameters.utils.ts'

interface IProps {
  dataRef: MutableRefObject<TLastValuesDataRef>
  widget: ITextWidget
}

const TextChart = (props: IProps) => {
  const [editorValue, setEditorValue] = useState<Descendant[]>([])
  const editor = withReact(createEditor())
  const parametersMap = useRecoilValue(parametersSelectors.getParametersMap)
  const unitPrefixesMap = useRecoilValue(parametersSelectors.getUnitsPrefixesMap)
  const unitsMap = useRecoilValue(parametersSelectors.getUnitsMap)

  const findParameterValue = (param: ITextWidgetParameter): string => {
    const parameter = parametersMap[param.parameterId]
    if (!parameter) return ''

    const deviceData = props.dataRef.current?.[param.deviceId]
    if (!deviceData) return ''

    const lastParameterValue = deviceData[param.parameterId]
    if (Number.isNaN(lastParameterValue)) return ''

    return parametersUtils.formatParameterValue({
      unitPrefixesMap: unitPrefixesMap,
      unitsMap: unitsMap,
      parameter: parameter,
      value: lastParameterValue,
    })
  }

  // Функция для рендеринга текста с подстановкой параметров.
  const renderText = (): Descendant[] => {
    const { text, parametersText } = props.widget.configuration.settings

    // Формируем объект параметров, где ключи — это $1, $2, ..., а значения — реальные данные
    const parameters = parametersText.reduce(
      (acc, param, index) => ({
        ...acc,
        [`$${index + 1}`]: findParameterValue(param), // Подставляем соответствующее значение параметра
      }),
      {} as Record<string, string>
    )

    // Обрабатываем текст типа Descendant, заменяя $1, $2, ..., значениями
    return text.map((node) => {
      if ('children' in node) {
        const updatedChildren = node.children.map((child) => {
          if ('text' in child) {
            let content = child.text
            Object.keys(parameters).forEach((key) => {
              content = content.replaceAll(new RegExp(`\\${key}`, 'g'), parameters[key] || '')
            })
            return {
              ...child,
              text: content,
            }
          }
          return child
        })

        return {
          ...node,
          children: updatedChildren,
        }
      }
      return node
    })
  }

  useSkipFirstRenderEffect(() => {
    if (props.widget.configuration.settings) {
      const updateText = renderText()
      setEditorValue(updateText)
    }
  }, [props.widget.configuration.settings])

  useEffect(() => {
    let animationFrameId: number

    const update = () => {
      const updateText = renderText()
      setEditorValue(updateText)
      animationFrameId = requestAnimationFrame(update)
    }

    animationFrameId = requestAnimationFrame(update)

    return () => cancelAnimationFrame(animationFrameId)
  }, [
    props.widget.configuration.settings.text,
    props.widget.configuration.settings.parametersText,
    props.widget.configuration.settings.title,
  ])

  return (
    <div className={css.wrapper}>
      <Slate editor={editor} initialValue={editorValue} key={JSON.stringify(editorValue)}>
        <Editable
          renderElement={RenderElement}
          renderLeaf={(props) => <RenderLeaf {...props} isRenderParameter={false} />}
          readOnly
        />
      </Slate>
    </div>
  )
}

export default memo(TextChart)
